import { useState, useRef, useEffect } from "react";
import "./HowItWorks.css";
import { Link } from "react-router-dom";

const STEPS = [
  {
    num: "01",
    icon: "📋",
    title: "Register Account",
    desc: "Create your free account in minutes with basic details",
    points: [
      "Fill in your full name, CNIC, and contact info",
      "Set a secure password for your dashboard",
      "Email verification sent instantly",
      "Account created — no fee required",
    ],
  },

  {
    num: "02",
    icon: "🔗",
    title: "Share Referral Link",
    desc: "Get your unique link and share it with your network",
    points: [
      "Your unique referral link is in your dashboard",
      "Share via WhatsApp, SMS, or social media",
      "Track every click and signup in real time",
      "Earn 10% bonus when they activate",
    ],
  },

  {
    num: "03",
    icon: "💰",
    title: "Earn Rewards & Bonuses",
    desc: "Multiple income streams paying every month",
    points: [
      "Monthly ROI on your investment package",
      "Quarterly profit sharing from real estate",
      "Rank rewards: Bronze → Diamond",
      "Withdraw anytime to bank or mobile wallet",
    ],
  },
];

/* ── Intersection observer hook for scroll-in ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function HowItWorks() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [sectionRef, inView] = useInView(0.1);

  const toggle = (i) => setActiveIndex((prev) => (prev === i ? null : i));

  return (
    <section
      className={`hiw${inView ? " hiw--visible" : ""}`}
      id="how-it-works"
      ref={sectionRef}
    >
      {/* radial glow */}
      <div className="hiw-glow" aria-hidden="true" />

      {/* ── Section header ── */}
      <div className="hiw-head">
        <span className="hiw-label">Simple Process</span>
        <h2 className="hiw-title">
          How It <span className="hiw-gold">Works</span>
        </h2>
        <div className="hiw-gline" />
        <p className="hiw-sub">
          Follow these five steps to go from new member to building a thriving
          income network.
        </p>
      </div>

      {/* ── Steps ── */}
      <div className="hiw-steps">
        {/* animated connector line */}
        {/* <div
          className="hiw-line-track"
          aria-hidden="true"
          style={{ left: "16.66%", width: "66.66%" }}
        />
        <div
          className={`hiw-line-fill${inView ? " hiw-line-fill--animate" : ""}`}
          aria-hidden="true"
          style={{ left: "16.66%", width: "66.66%" }}
        /> */}

        <div className="hiw-grid">
          {STEPS.map((step, i) => (
            <div
              key={step.num}
              className={`hiw-card${activeIndex === i ? " hiw-card--active" : ""}`}
              style={{ "--delay": `${i * 0.12}s` }}
              onClick={() => toggle(i)}
              role="button"
              tabIndex={0}
              aria-expanded={activeIndex === i}
              onKeyDown={(e) => e.key === "Enter" && toggle(i)}
            >
              {/* icon circle */}
              <div className="hiw-circle-wrap">
                <div
                  className="hiw-circle"
                  style={{
                    "--pd": `${i * 0.2}s`,
                    "--pd2": `${i * 0.2 + 0.3}s`,
                  }}
                >
                  <span className="hiw-step-num">STEP {step.num}</span>
                  <span className="hiw-icon">{step.icon}</span>
                </div>

                {/* number badge outside circle */}
                <div className="hiw-badge">{step.num}</div>
              </div>

              <h3 className="hiw-card-title">{step.title}</h3>
              <p className="hiw-card-desc">{step.desc}</p>

              {/* expandable detail panel */}
              <div className="hiw-detail" aria-hidden={activeIndex !== i}>
                <div className="hiw-detail-inner">
                  <ul>
                    {step.points.map((pt, j) => (
                      <li key={j}>{pt}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* expand hint */}
              <div className="hiw-hint">
                {activeIndex === i
                  ? "Click to collapse"
                  : "Click to learn more"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="hiw-cta">
        <p>Ready to start your journey to financial freedom?</p>
        <Link to="/join-us" className="hiw-btn">
          Start Now — It's Free
        </Link>
      </div>
    </section>
  );
}
